import React, { useState } from 'react';
import { Button } from '../ui/Button';
import {
    downloadImageWithWatermark,
    shareOnFacebook,
    shareOnTwitter,
    shareOnWhatsApp,
    shareOnLinkedIn,
    copyImageLink,
    shareNative
} from '../../services/socialShareService';
import './SocialShareButtons.css';

interface SocialShareButtonsProps {
    imageUrl: string;
    postText?: string;
    postId?: string;
}

export const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({
    imageUrl,
    postText = '',
    postId = ''
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleDownload = async () => {
        try {
            setDownloading(true);
            const filename = `nutrisync-post-${postId || Date.now()}.png`;
            await downloadImageWithWatermark(imageUrl, filename);
            alert('✅ Imagem baixada com marca d\'água NutriSync!');
        } catch (error) {
            console.error('Erro ao baixar imagem:', error);
            alert('❌ Erro ao baixar imagem. Tente novamente.');
        } finally {
            setDownloading(false);
        }
    };

    const handleCopyLink = async () => {
        try {
            await copyImageLink(imageUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Erro ao copiar link:', error);
            alert('❌ Erro ao copiar link.');
        }
    };

    const handleNativeShare = async () => {
        const success = await shareNative(imageUrl, postText, 'NutriSync - Minha Jornada');
        if (!success) {
            // Se não suportar Web Share API, mostrar menu de opções
            setShowMenu(true);
        }
    };

    const hasNativeShare = typeof navigator !== 'undefined' && 'share' in navigator;

    return (
        <div className="social-share-container">
            <Button
                onClick={() => setShowMenu(!showMenu)}
                style={{
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white',
                    fontSize: '13px',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.2s ease'
                }}
                title="Compartilhar nas redes sociais"
            >
                <span>📤</span>
                <span>Compartilhar</span>
            </Button>

            {showMenu && (
                <>
                    <div
                        className="social-share-overlay"
                        onClick={() => setShowMenu(false)}
                    />
                    <div className="social-share-menu">
                        <div className="social-share-header">
                            <h4>Compartilhar Imagem</h4>
                            <button
                                className="social-share-close"
                                onClick={() => setShowMenu(false)}
                            >
                                ✕
                            </button>
                        </div>

                        <p className="social-share-info">
                            💡 A imagem será compartilhada com a marca d'água do NutriSync
                        </p>

                        <div className="social-share-buttons-grid">
                            {/* Botão de compartilhamento nativo (mobile) */}
                            {hasNativeShare && (
                                <button
                                    className="social-share-button native"
                                    onClick={handleNativeShare}
                                >
                                    <span className="social-icon">📱</span>
                                    <span>Compartilhar</span>
                                </button>
                            )}

                            {/* Facebook */}
                            <button
                                className="social-share-button facebook"
                                onClick={() => {
                                    shareOnFacebook(imageUrl, postText);
                                    setShowMenu(false);
                                }}
                            >
                                <span className="social-icon">📘</span>
                                <span>Facebook</span>
                            </button>

                            {/* Twitter/X */}
                            <button
                                className="social-share-button twitter"
                                onClick={() => {
                                    shareOnTwitter(imageUrl, postText);
                                    setShowMenu(false);
                                }}
                            >
                                <span className="social-icon">🐦</span>
                                <span>Twitter</span>
                            </button>

                            {/* WhatsApp */}
                            <button
                                className="social-share-button whatsapp"
                                onClick={() => {
                                    shareOnWhatsApp(imageUrl, postText);
                                    setShowMenu(false);
                                }}
                            >
                                <span className="social-icon">💬</span>
                                <span>WhatsApp</span>
                            </button>

                            {/* LinkedIn */}
                            <button
                                className="social-share-button linkedin"
                                onClick={() => {
                                    shareOnLinkedIn(imageUrl);
                                    setShowMenu(false);
                                }}
                            >
                                <span className="social-icon">💼</span>
                                <span>LinkedIn</span>
                            </button>

                            {/* Download */}
                            <button
                                className="social-share-button download"
                                onClick={handleDownload}
                                disabled={downloading}
                            >
                                <span className="social-icon">{downloading ? '⏳' : '⬇️'}</span>
                                <span>{downloading ? 'Baixando...' : 'Baixar'}</span>
                            </button>

                            {/* Copiar Link */}
                            <button
                                className="social-share-button copy"
                                onClick={handleCopyLink}
                            >
                                <span className="social-icon">{copied ? '✅' : '🔗'}</span>
                                <span>{copied ? 'Copiado!' : 'Copiar Link'}</span>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
